'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import ContentEditorContainer from 'ContentEditorUI/containers/ContentEditorContainer';
import AppReadyContainer from 'ContentEditorUI/containers/AppReadyContainer';
import { fetchContentSchema as fetchContentSchemaActionCreator } from 'ContentEditorUI/redux/actions/appActions';
import { fetchInitialUserAttributes as fetchInitialUserAttributesActionCreator } from 'ContentEditorUI/redux/actions/userActions';
import { watchForI18nReady } from 'ContentEditorUI/redux/actions/i18n';
import { getPortalId } from 'ContentEditorUI/redux/selectors/authSelectors';
import { fetchPortalScopes, fetchPortalInfo } from 'ContentEditorUI/redux/actions/portalActions';
import { ensureEarlyRequestedAttributesMatch } from 'ContentEditorUI/lib/userAndLocalCacheSettings/helpers';
import withPathParams from 'ContentEditorUI/utils/withPathParams';
import ApplicationErrorContainer from './ApplicationErrorContainer';
import EditorConfigSingleton from 'ContentEditorUI/EditorConfigSingleton';
import { fetchCategorizationData } from 'ContentEditorUI/redux/actions/categorizationActions';
import EmbeddedEditorWrapper from 'campaigns-lib/embedded/EmbeddedEditorWrapper';

// ApplicationContainer: A top-level component which owns:
//  - Rendering loading/skeleton UI
//  - Common data fetching for all editor apps (until scalable editors initialRequests api takes over)
//  - Rendering startup/fatal errors (from common data fetching)
//  - The first loading spinner before we can render any UI (blocking on the common data fetching)
import { jsx as _jsx } from "react/jsx-runtime";
const mapStateToProps = state => ({
  portalId: getPortalId(state)
});
const mapDispatchToProps = {
  fetchContentSchema: fetchContentSchemaActionCreator,
  fetchInitialUserAttributes: fetchInitialUserAttributesActionCreator,
  fetchPortalScopes,
  fetchPortalInfo,
  watchForI18nReady,
  fetchCategorization: fetchCategorizationData
};
class ApplicationContainer extends Component {
  componentDidMount() {
    this.addPageClassNames();
    this.fetchData();
  }
  addPageClassNames() {
    const {
      pageClassName
    } = this.props;
    if (pageClassName) {
      pageClassName.split(' ').forEach(className => document.documentElement.classList.add(className));
    }
  }
  fetchData() {
    const {
      fetchContentSchema,
      fetchModuleSchemas,
      includeUsedModuleSchemas,
      // eslint-disable-next-line no-shadow
      watchForI18nReady,
      preProcessSchema,
      userAttributesToFetch,
      fetchInitialUserAttributes,
      fetchCategorization
    } = this.props;
    const {
      contentId
    } = this.props.params;

    // Traditionally there is where the module and content
    // schemas were fetched for page-editor-ui / blog-editor-ui
    // Moving forward on "scalable editor" these requests are configured in the "editor configs" initialRequests api. Putting in checks here to make sure these are only made if we are NOT on scalable editor for this given app. Eventually these should be removed.

    if (EditorConfigSingleton.getIsOnScalableEditor()) {
      fetchCategorization();
    }
    let contentSchemaPromise;
    if (!EditorConfigSingleton.getIsOnScalableEditor()) {
      contentSchemaPromise = fetchContentSchema(contentId, preProcessSchema, includeUsedModuleSchemas);
    }
    if (fetchModuleSchemas && !EditorConfigSingleton.getIsOnScalableEditor()) {
      fetchModuleSchemas(contentSchemaPromise, contentId);
    } else if (!EditorConfigSingleton.getIsOnScalableEditor()) {
      contentSchemaPromise.catch(err => {
        setTimeout(() => {
          throw err;
        });
      });
    }
    if (userAttributesToFetch) {
      ensureEarlyRequestedAttributesMatch(userAttributesToFetch);
      fetchInitialUserAttributes(userAttributesToFetch);
    }
    this.props.fetchPortalScopes();
    this.props.fetchPortalInfo();
    watchForI18nReady();
  }
  render() {
    const {
      children,
      hasSkeleton,
      hasRequiredAccess,
      upgradeSource,
      ErrorWrapperComponent,
      params
    } = this.props;
    const fallbackComponent = !hasSkeleton && /*#__PURE__*/_jsx(LoadingOverlay, {
      "data-test-id": "loading-content-model"
    });
    return /*#__PURE__*/_jsx(EmbeddedEditorWrapper, {
      children: /*#__PURE__*/_jsx(ApplicationErrorContainer, {
        hasSkeleton: hasSkeleton,
        ErrorWrapperComponent: ErrorWrapperComponent,
        hasRequiredAccess: hasRequiredAccess,
        upgradeSource: upgradeSource,
        routerParams: params,
        children: /*#__PURE__*/_jsx(AppReadyContainer, {
          fallbackComponent: fallbackComponent,
          children: /*#__PURE__*/_jsx(ContentEditorContainer, {
            children: children
          })
        })
      })
    });
  }
}
ApplicationContainer.propTypes = {
  children: PropTypes.node,
  ErrorWrapperComponent: PropTypes.elementType,
  params: PropTypes.object.isRequired,
  fetchPortalScopes: PropTypes.func.isRequired,
  fetchPortalInfo: PropTypes.func.isRequired,
  includeUsedModuleSchemas: PropTypes.bool,
  hasRequiredAccess: PropTypes.bool,
  upgradeSource: PropTypes.string,
  pageClassName: PropTypes.string,
  fetchContentSchema: PropTypes.func.isRequired,
  fetchInitialUserAttributes: PropTypes.func.isRequired,
  fetchModuleSchemas: PropTypes.func,
  userAttributesToFetch: PropTypes.array,
  watchForI18nReady: PropTypes.func.isRequired,
  preProcessSchema: PropTypes.func,
  hasSkeleton: PropTypes.bool,
  fetchCategorization: PropTypes.func.isRequired
};
ApplicationContainer.defaultProps = {
  hasSkeleton: false,
  includeUsedModuleSchemas: false
};
export default connect(mapStateToProps, mapDispatchToProps)(withPathParams(ApplicationContainer));